import { Fragment, FunctionComponent, useState } from 'react';
import {
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    Drawer,
    Fade,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { CardListRow } from '@/components/Card/CardListRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@heroicons/react/24/solid/PencilIcon';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { LoadingContainer } from '@/components/Layout/Container';
import { useDeleteUserMutation, useGetUsersQuery } from '@/api/v2/user';
import { useAuthentication } from '@/hooks/useAuthentication';
import { createPlainCreateUser, User, UserType } from '@/models/user';
import { useNotification } from '@starjack/features/notifications';
import { UserDrawerContent } from '@/pages/userManagement/components/UserDrawerContent';
import { useGetLiftOperatorsQuery } from '@/api/v2/liftOperator';

export const UserManagementContent: FunctionComponent = () => {
    const theme = useTheme();
    const {
        liftOperatorData: { liftOperatorId, skiOperator },
        userType,
    } = useAuthentication();
    const { data: users } = useGetUsersQuery({ liftOperatorId });
    const { data: liftOperators = [] } = useGetLiftOperatorsQuery();
    const [deleteUser] = useDeleteUserMutation();

    // Find the current lift operator by ID
    const currentLiftOperator = liftOperators?.find(
        (operator) => operator.id === liftOperatorId
    );
    const liftOperatorName = currentLiftOperator?.name?.de || skiOperator;
    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        user: User;
    }>(null);
    const { addNotification } = useNotification();
    const [deleteLoadingId, setDeleteLoadingId] = useState<string>('');

    const onDeleteUser = (userId: string) => {
        setDeleteLoadingId(userId);
        deleteUser({ liftOperatorId, userId })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Benutzer erfolgreich gelöscht',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Löschen. Versuchen Sie es später noch einmal',
                    type: 'error',
                });
            })
            .finally(() => {
                setDeleteLoadingId(null);
            });
    };

    const onCloseDrawer = () => {
        setDrawerConfig({
            isOpen: false,
            user: null,
        });
    };

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Benutzerverwaltung
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Container
                    sx={{
                        mt: 3,
                        ml: 'unset',
                    }}
                    maxWidth='lg'
                >
                    <Card>
                        <CardContent
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant='h6'>
                                Angelegte Benutzer
                            </Typography>

                            <Button
                                onClick={() => {
                                    setDrawerConfig({
                                        isOpen: true,
                                        user: null,
                                    });
                                }}
                                sx={{
                                    ml: 'auto',
                                }}
                                variant='contained'
                            >
                                + Neuen Benutzer anlegen
                            </Button>
                        </CardContent>

                        <CardListRow
                            sx={{
                                backgroundColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <Typography sx={{ width: '50%', fontWeight: 600 }}>
                                Name
                            </Typography>
                            <Typography sx={{ width: '50%', fontWeight: 600 }}>
                                Firma
                            </Typography>
                        </CardListRow>

                        {userType === UserType.LiftOperator && (
                            <CardListRow
                                sx={(theme) => ({
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: theme.palette.neutral[100],
                                })}
                            >
                                <Typography
                                    sx={{
                                        width: '50%',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {skiOperator}
                                    <Typography
                                        component='span'
                                        variant='caption'
                                        sx={{
                                            ml: 1,
                                            color: 'text.secondary',
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {liftOperatorName}
                                    </Typography>
                                </Typography>

                                <Typography
                                    sx={{
                                        width: '30%',
                                    }}
                                >
                                    -
                                </Typography>

                                <Stack
                                    sx={{
                                        ml: 'auto',
                                        alignItems: 'center',
                                    }}
                                    direction='row'
                                    spacing={2}
                                >
                                    <IconButton
                                        onClick={() => {
                                            setDrawerConfig({
                                                isOpen: true,
                                                user: {
                                                    id: 'lift-operator',
                                                    username: skiOperator,
                                                    password: '',
                                                    liftOperatorId,
                                                    permissions: [],
                                                    company: '',
                                                } as User,
                                            });
                                        }}
                                    >
                                        <SvgIcon>
                                            <EditIcon />
                                        </SvgIcon>
                                    </IconButton>
                                </Stack>
                            </CardListRow>
                        )}

                        {userType === UserType.LiftOperator &&
                            users &&
                            users.length > 0 && (
                                <Divider
                                    sx={{
                                        width: '100%',
                                    }}
                                />
                            )}

                        {users?.map((user, index) => (
                            <Fragment key={index}>
                                <CardListRow
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            width: '50%',
                                        }}
                                    >
                                        {user.username}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            width: '30%',
                                        }}
                                    >
                                        {user.company || '-'}
                                    </Typography>

                                    <Stack
                                        sx={{
                                            ml: 'auto',
                                            alignItems: 'center',
                                        }}
                                        direction='row'
                                        spacing={2}
                                    >
                                        <IconButton
                                            onClick={() => {
                                                setDrawerConfig({
                                                    isOpen: true,
                                                    user,
                                                });
                                            }}
                                        >
                                            <SvgIcon>
                                                <EditIcon />
                                            </SvgIcon>
                                        </IconButton>

                                        <IconButton>
                                            <SvgIcon>
                                                <DeleteIcon
                                                    onClick={() =>
                                                        onDeleteUser(user.id)
                                                    }
                                                />
                                            </SvgIcon>
                                        </IconButton>
                                    </Stack>
                                    <Fade in={deleteLoadingId === user.id}>
                                        <div>
                                            <LoadingContainer cover />
                                        </div>
                                    </Fade>
                                </CardListRow>

                                {index < users.length - 1 && (
                                    <Divider
                                        sx={{
                                            width: '100%',
                                        }}
                                    />
                                )}
                            </Fragment>
                        ))}
                    </Card>

                    <Drawer
                        anchor='right'
                        open={drawerConfig?.isOpen}
                        onClose={onCloseDrawer}
                    >
                        <UserDrawerContent
                            user={
                                drawerConfig?.user ||
                                createPlainCreateUser(liftOperatorId)
                            }
                            onClose={onCloseDrawer}
                            isLiftOperatorEdit={
                                drawerConfig?.user?.id === 'lift-operator'
                            }
                        />
                    </Drawer>
                </Container>
            </Stack>
        </>
    );
};
